import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

import {
  getCourseByIdForUsers,
  getCoursesState,
  resetUserCourse,
} from '../../../store/V2/CoursesSlice';
import {
  ISection,
  getSectionsState,
  getUserCourseSectionsByCourseId,
  resetNewlyAddedSectionId,
  resetUserCourseSections,
  updateCourseSectionState,
} from '../../../store/V2/SectionSlice';
import { TP_COLORS } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../components/BreadCrumb/Breadcrumbs';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import SubscriptionCard from './components/SubscriptionCard';
import ClassDetailViewer from './components/ClassDetailViewer';
import SectionItem, {
  TSubSectionCreateNewItemRequestData,
} from './components/SectionItem';
import ClassCancellationModal from './modals/ClassCancellationModal';
import ClassRescheduleModal from './modals/ClassRescheduleModal';
import AddHomeworkButton from './components/AddHomeworkButton';
import { EditModeSwitch } from '../Portal/components/Header';
import AddHomeworkModal from './modals/AddHomeworkModal';
import { checkUserPermission } from '../../../helper';
import { getAuthState } from '../../../store/AuthSlice';
import {
  getConfigState,
  toggleHomeworkModel,
} from '../../../store/ConfigSlice';
import Chat from '../Chat';
import { USER_PERMISSION_INDIVIDUALS } from '../../../store/V2/RolesSlice';
import { getChatState, resetCourseChatData } from '../../../store/V2/ChatSlice';
import { getTenantsState } from '../../../store/V2/TenantsSlice';

const defaultDeviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const ViewCoursePage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const urlParams = useParams();
  const location = useLocation();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));
  const searchParams = new URLSearchParams(location.search);
  const scrollId = searchParams.get('scroll');

  // Store
  const coursesState = useAppSelector(getCoursesState);
  const sectionsState = useAppSelector(getSectionsState);
  const authState = useAppSelector(getAuthState);
  const configState = useAppSelector(getConfigState);
  const chatState = useAppSelector(getChatState);
  const tenantState = useAppSelector(getTenantsState);

  const { user: authUser } = authState;
  const { appTenant } = tenantState;
  const { tutorHomeworkMode } = configState;

  // State
  const [alertVisibility, setAlertVisibility] = useState(true);
  const [openedSections, setOpenedSections] = useState<Array<string>>([]);
  const [isClassListCollapseOpen, setClassListCollapseOpen] = useState(true);
  const [isClassDetailsCollapseOpen, setClassDetailsCollapseOpen] = useState(
    true
  );
  const [isAutoScrolled, setIsAutoScrolled] = useState(false);

  const [classCancellationModalData, setClassCancellationModalData] = useState<{
    open: boolean;
    section?: ISection;
  }>({
    open: false,
  });

  const [classRescheduleModalData, setClassRescheduleModalData] = useState<{
    open: boolean;
    section?: ISection;
  }>({
    open: false,
  });

  const [homeworkModalData, setHomeworkModalData] = useState<{
    open: boolean;
    prevSection?: ISection;
    parentSection?: TSubSectionCreateNewItemRequestData['parentSection'];
  }>({
    open: false,
  });

  const courseId = urlParams.id;
  const courseData = coursesState.userCourse;
  const courseSections = sectionsState.userCourseSections || [];
  const questyStringParams = new URLSearchParams(location.search);
  const isSubscriptionVisible =
    courseData &&
    (courseData.pricings || []).length &&
    courseData.selfEnrolement &&
    !courseData.isSubscribed;

  const isChatVisible =
    chatState.isConnected &&
    courseData &&
    courseData.isSubscribed &&
    !isSubscriptionVisible &&
    courseData.chat &&
    courseData.chat.isChatEnabled;

  const isAllSectionsOpened = courseSections.every((item) =>
    openedSections.some((id) => id === item._id)
  );

  let numberOfClassesAndDurationText = '0 Class';

  if (courseData && courseData.noOfClasses) {
    numberOfClassesAndDurationText = `${courseData.noOfClasses} Class`;

    if (courseData.noOfClasses > 1) {
      numberOfClassesAndDurationText += 'es';
    }

    if (courseData.duration) {
      const _totalDuration = courseData.noOfClasses * courseData.duration;
      const _hours = Math.floor(_totalDuration / 60);
      const _mins = _totalDuration % 60;

      let _formatedDuration = '';

      if (_hours) {
        _formatedDuration = `${_hours} hr`;

        if (_mins) {
          _formatedDuration += ` ${_mins} min`;
        }
      } else {
        _formatedDuration += ` ${_mins} min`;
      }

      numberOfClassesAndDurationText += ` | ${_formatedDuration}`;
    }
  }

  const homeworkCreatePermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      homework: [USER_PERMISSION_INDIVIDUALS.HOMEWORK_CREATE],
    },
  });

  const isCourseTutor = courseData?.tutors?.some(
    (tutor) => tutor?.tutor?._id === authUser?._id
  );

  useEffect(() => {
    if (courseId) {
      dispatch(
        getCourseByIdForUsers({
          id: courseId,
          deviceTimezone: defaultDeviceTimezone,
        })
      );

      dispatch(getUserCourseSectionsByCourseId(courseId));
    }

    return () => {
      dispatch(resetUserCourse());
      dispatch(resetUserCourseSections());
      dispatch(resetCourseChatData());
    };
  }, [courseId]);

  useEffect(() => {
    setOpenedSections((prevState) => {
      const _newList = [...prevState];

      if (sectionsState.newlyAddedSectionId) {
        _newList.push(sectionsState.newlyAddedSectionId);
      }

      dispatch(resetNewlyAddedSectionId());

      return _newList;
    });
  }, [sectionsState.newlyAddedSectionId]);

  useEffect(() => {
    if (scrollId && courseSections.length > 0 && !isAutoScrolled) {
      const newOpenedSections: string[] = [];

      const findAndExpand = (sections, targetId) => {
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < sections.length; i++) {
          if (sections[i]._id === targetId) {
            newOpenedSections.push(sections[i]._id);
            return true;
          }

          if (sections[i].sections && sections[i].sections.length > 0) {
            const found = findAndExpand(sections[i].sections, targetId);
            if (found) {
              newOpenedSections.push(sections[i]._id);
              return true;
            }
          }
        }

        return false;
      };
      findAndExpand(courseSections, scrollId);
      setOpenedSections(newOpenedSections);
      setIsAutoScrolled(true);
      setTimeout(() => {
        const buttonToScroll = document.getElementById(scrollId);
        if (buttonToScroll) {
          buttonToScroll.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 1000);
    }
  }, [courseSections]);

  const toggleClassDetailsCollapse = () => {
    setClassDetailsCollapseOpen(!isClassDetailsCollapseOpen);
  };

  const toggleClassListCollapse = () => {
    setClassListCollapseOpen(!isClassListCollapseOpen);
  };

  const handleSectionCollapse = (section: ISection) => {
    let newOpenedSections = [...openedSections];

    const match = newOpenedSections.some((item) => item === section._id);

    if (match) {
      newOpenedSections = newOpenedSections.filter(
        (item) => item !== section._id
      );
    } else {
      newOpenedSections.push(section._id);
    }

    setOpenedSections(newOpenedSections);
  };

  const handleCollapseAllSections = () => {
    setOpenedSections((prevState) => {
      if (isAllSectionsOpened) {
        return [];
      }

      const newList = [...prevState];

      courseSections.forEach((item) => {
        if (!newList.some((id) => id === item._id)) {
          newList.push(item._id);
        }
      });

      return newList;
    });
  };

  const openClassCancellationModal = (section: ISection) => {
    setClassCancellationModalData({
      section,
      open: true,
    });
  };

  const closeClassCancellationModal = () => {
    setClassCancellationModalData({
      open: false,
    });
  };

  const openClassRescheduleModal = (section: ISection) => {
    setClassRescheduleModalData({
      section,
      open: true,
    });
  };

  const closeClassRescheduleModal = () => {
    setClassRescheduleModalData({
      open: false,
    });
  };

  const openAddHomeworkModalForParent = (section: ISection) => {
    setHomeworkModalData({
      prevSection: section,
      open: true,
    });
  };

  const openAddHomeworkModalForSection = (
    data: TSubSectionCreateNewItemRequestData
  ) => {
    setHomeworkModalData({
      open: true,
      parentSection: data.parentSection,
    });
  };

  const closeAddHomeworkModal = () => {
    setHomeworkModalData({
      open: false,
    });
  };

  const constructBreadcrumbOptions = () => {
    const options: Array<BreadcrumbOption> = [
      {
        type: 'link',
        title: 'Home',
        link: `${process.env.PUBLIC_URL}`,
      },
      {
        type: 'link',
        title: 'Courses',
        link: `${process.env.PUBLIC_URL}/portal/courses`,
      },
    ];

    if (courseData) {
      options.push({
        type: 'link',
        title: courseData.title,
        highlight: true,
      });
    }

    return options;
  };

  const renderAlerts = () => {
    if (courseData) {
      if (
        questyStringParams.get('paymentStatus') === 'success' &&
        courseData.isSubscribed
      ) {
        return (
          <Collapse in={alertVisibility}>
            <Alert
              icon={false}
              color="success"
              onClose={() => setAlertVisibility(false)}
              sx={{
                backgroundColor: 'success.light',
                color: 'success.dark',
                mb: '15px',
              }}
            >
              Course Subscribed Successfully!
            </Alert>
          </Collapse>
        );
      }

      if (
        questyStringParams.get('paymentStatus') === 'canceled' &&
        !courseData.isSubscribed
      ) {
        return (
          <Collapse in={alertVisibility}>
            <Alert
              icon={false}
              color="warning"
              onClose={() => setAlertVisibility(false)}
              sx={{
                backgroundColor: 'warning.light',
                color: 'warning.dark',
                mb: '15px',
              }}
            >
              Course Subscription Canceled!
            </Alert>
          </Collapse>
        );
      }

      if (
        questyStringParams.get('paymentStatus') === 'failed' &&
        !courseData.isSubscribed
      ) {
        return (
          <Collapse in={alertVisibility}>
            <Alert
              icon={false}
              color="error"
              onClose={() => setAlertVisibility(false)}
              sx={{
                backgroundColor: 'error.light',
                color: 'error.dark',
                mb: '15px',
              }}
            >
              Course Subscription Failed!
            </Alert>
          </Collapse>
        );
      }
    }

    return null;
  };

  const breadcrumbOptions = constructBreadcrumbOptions();

  return (
    <>
      <Container maxWidth="lg">
        <div style={{ padding: '30px 0' }}>
          <Breadcrumbs
            options={breadcrumbOptions}
            textColor="#647488"
            showDropDown={isXsScreen}
          />
        </div>
        {renderAlerts()}
        {coursesState.status === 'loading' && !courseData ? (
          <BasicLoader />
        ) : null}
        <Grid container columnSpacing="20px" rowGap={2} mb="15px">
          <Grid item xs={12} sm={12} md>
            {courseData ? (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" flexGrow={1}>
                    <IconButton
                      style={{ marginRight: '5px' }}
                      onClick={toggleClassDetailsCollapse}
                    >
                      {isClassDetailsCollapseOpen ? (
                        <ExpandLessIcon style={{ color: TP_COLORS.black }} />
                      ) : (
                        <ExpandMoreIcon style={{ color: TP_COLORS.black }} />
                      )}
                    </IconButton>
                    <Typography
                      component="h1"
                      fontSize="25px"
                      lineHeight="30px"
                      fontWeight={700}
                    >
                      {courseData.title}
                    </Typography>
                  </Stack>
                </Stack>
                <Collapse in={isClassDetailsCollapseOpen}>
                  <section
                    style={{ padding: `0 ${isXsScreen ? '15px' : '50px'}` }}
                  >
                    <div style={{ marginBottom: '30px' }}>
                      <Box
                        sx={{
                          color: '#647488',
                          pt: '27px',
                          '& p': {
                            margin: 0,
                          },
                        }}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                          __html: courseData.description || '',
                        }}
                      />
                    </div>
                    <div>
                      <ClassDetailViewer course={courseData} />
                    </div>
                  </section>
                </Collapse>
                <div
                  style={{
                    height: '1px',
                    borderBottom: '1px dotted #94A3B8',
                    marginTop: '40px',
                  }}
                />
                <Stack
                  component="section"
                  mt="40px"
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  mb="24px"
                >
                  <Stack>
                    <Stack direction="row" alignItems="center">
                      <IconButton
                        style={{ marginRight: '5px' }}
                        onClick={toggleClassListCollapse}
                      >
                        {isClassListCollapseOpen ? (
                          <ExpandLessIcon style={{ color: TP_COLORS.black }} />
                        ) : (
                          <ExpandMoreIcon style={{ color: TP_COLORS.black }} />
                        )}
                      </IconButton>
                      <Typography
                        component="h2"
                        variant="h5"
                        fontSize="25px"
                        fontWeight={700}
                      >
                        Class Details
                      </Typography>
                    </Stack>
                    <Typography
                      variant="caption"
                      fontSize="14px"
                      fontWeight={500}
                      color="#647488"
                      ml="48px"
                      mt="-6px"
                    >
                      {numberOfClassesAndDurationText}
                    </Typography>
                  </Stack>
                  <Box>
                    {homeworkCreatePermission && isCourseTutor && (
                      <FormControlLabel
                        label="Add Homework"
                        labelPlacement="start"
                        sx={{ color: '#1E293B', textAlign: 'right' }}
                        control={
                          <EditModeSwitch
                            checked={tutorHomeworkMode}
                            onChange={() => dispatch(toggleHomeworkModel())}
                            sx={{ ml: '10px', mr: '20px' }}
                          />
                        }
                      />
                    )}
                    {courseSections.length ? (
                      <Button
                        variant="text"
                        color="info"
                        size="small"
                        onClick={handleCollapseAllSections}
                      >
                        {isAllSectionsOpened
                          ? 'Collapse all sections'
                          : 'Expand all sections'}
                      </Button>
                    ) : null}
                  </Box>
                </Stack>
                {/* <Stack direction="row" alignItems="center" mt="30px" mb="15px">
                  <IconButton
                    style={{ marginRight: '5px' }}
                    onClick={toggleClassListCollapse}
                  >
                    {isClassListCollapseOpen ? (
                      <ExpandLessIcon style={{ color: TP_COLORS.black }} />
                    ) : (
                      <ExpandMoreIcon style={{ color: TP_COLORS.black }} />
                    )}
                  </IconButton>
                  <Stack flexGrow={1}>
                    <Typography variant="h5" fontSize="25px" fontWeight={700}>
                      Class Details
                    </Typography>
                    <Typography
                      variant="caption"
                      fontSize="14px"
                      fontWeight={500}
                      color="#647488"
                    >
                      {numberOfClassesAndDurationText}
                    </Typography>
                  </Stack>
                  
                </Stack> */}
                <Collapse in={isClassListCollapseOpen}>
                  {sectionsState.status === 'loading' &&
                  !courseSections.length ? (
                    <div style={{ marginBottom: '15px' }}>
                      <BasicLoader />
                    </div>
                  ) : null}
                  <Stack component="section" spacing="8px">
                    {courseSections.map((item, index) => {
                      const _keyName = `${index}`;
                      const _orderNumber = index + 1;
                      const _collapse = openedSections.some(
                        (itemId) => itemId === item._id
                      );

                      return (
                        <Stack id={_keyName} key={_keyName}>
                          <SectionItem
                            isOneToOne={courseData.isOneToOne || false}
                            isCourseSubscribed={
                              courseData.isSubscribed || false
                            }
                            openedSections={openedSections}
                            keyName={_keyName}
                            orderNumber={_orderNumber}
                            section={item}
                            collapse={_collapse}
                            toggleCollapse={handleSectionCollapse}
                            onCancelClass={openClassCancellationModal}
                            onRescheduleClass={openClassRescheduleModal}
                            onAddNewItem={openAddHomeworkModalForSection}
                          />
                          <AddHomeworkButton
                            disabled={sectionsState.status === 'loading'}
                            onClick={() => openAddHomeworkModalForParent(item)}
                          />
                        </Stack>
                      );
                    })}
                  </Stack>
                </Collapse>
              </>
            ) : null}
          </Grid>
          {courseData && isSubscriptionVisible ? (
            <Grid item style={{ width: '303px' }}>
              <section style={{ position: 'sticky', top: 'calc(64px + 15px)' }}>
                <SubscriptionCard course={courseData} />
              </section>
            </Grid>
          ) : null}
          {isChatVisible ? <Chat isXsScreen={isXsScreen} /> : null}
        </Grid>
      </Container>
      {/* MODALS */}
      {/* CLASS CANCELLATION */}
      {classCancellationModalData &&
      classCancellationModalData.open &&
      classCancellationModalData.section &&
      courseData &&
      courseData.identifiedCourseUserRole ? (
        <ClassCancellationModal
          open={classCancellationModalData.open}
          onClose={closeClassCancellationModal}
          sectionId={classCancellationModalData.section._id}
          sectionType={classCancellationModalData.section.type}
          identifiedUserRole={courseData.identifiedCourseUserRole}
          onSubmitted={(section) => {
            dispatch(
              updateCourseSectionState({
                section,
                id: section._id,
              })
            );

            closeClassCancellationModal();
          }}
        />
      ) : null}
      {classRescheduleModalData &&
      classRescheduleModalData.open &&
      classRescheduleModalData.section &&
      courseData &&
      courseData.identifiedCourseUserRole ? (
        <ClassRescheduleModal
          open={classRescheduleModalData.open}
          onClose={closeClassRescheduleModal}
          sectionId={classRescheduleModalData.section._id}
          identifiedUserRole={courseData.identifiedCourseUserRole}
          onSubmitted={(section) => {
            dispatch(
              updateCourseSectionState({
                section,
                id: section._id,
              })
            );

            closeClassRescheduleModal();
          }}
        />
      ) : null}
      {/* HOMEWORK */}
      {homeworkModalData.open ? (
        <AddHomeworkModal
          open={homeworkModalData.open}
          onClose={closeAddHomeworkModal}
          parentSectionItemData={homeworkModalData.parentSection}
          prevSection={homeworkModalData.prevSection}
        />
      ) : null}
    </>
  );
};

export default ViewCoursePage;
